import React from 'react';
import {Image, Pressable, StyleSheet, Text, View} from 'react-native';
import {FontAwesome, MaterialCommunityIcons} from '@expo/vector-icons';
import ENV from '../../../environment';

const rupayLogo = require('@/Assets/Images/Svgs/Payments/rupay.svg');

export type InternationalPaymentMethod =
  | 'visa'
  | 'rupay'
  | 'mastercard'
  | 'amex'
  | 'diner'
  | 'apple_pay'
  | 'paypal';

type Props = {
  value?: InternationalPaymentMethod;
  onChange: (method?: InternationalPaymentMethod) => void;
};

const PAYPAL_SURCHARGE_PCT =
  Math.round((ENV.PAYPAL_SURCHARGE_RATE || 0) * 1000) / 10;

type Option = {
  value: InternationalPaymentMethod;
  label: string;
  surchargePct: number;
};

const OPTIONS: Option[] = [
  // Temporarily disabled — re-enable when those rails are live.
  // {value: 'visa', label: 'Visa', surchargePct: 0},
  // {value: 'mastercard', label: 'Mastercard', surchargePct: 0},
  // {value: 'amex', label: 'Amex', surchargePct: 0},
  // {value: 'rupay', label: 'RuPay', surchargePct: 0},
  // {value: 'diner', label: 'Diners', surchargePct: PAYPAL_SURCHARGE_PCT},
  // {value: 'apple_pay', label: 'Apple Pay', surchargePct: PAYPAL_SURCHARGE_PCT},
  {value: 'paypal', label: 'PayPal', surchargePct: PAYPAL_SURCHARGE_PCT},
];

const BrandMark = ({id}: {id: InternationalPaymentMethod}) => {
  if (id === 'visa') {
    return (
      <View style={[styles.mark, styles.markVisa]}>
        <Text style={styles.markVisaText}>VISA</Text>
      </View>
    );
  }
  if (id === 'mastercard') {
    return (
      <View style={[styles.mark, styles.markMc]}>
        <View style={[styles.mcCircle, {backgroundColor: '#EB001B'}]} />
        <View
          style={[
            styles.mcCircle,
            {backgroundColor: '#F79E1B', marginLeft: -6},
          ]}
        />
      </View>
    );
  }
  if (id === 'amex') {
    return (
      <View style={[styles.mark, styles.markAmex]}>
        <Text style={styles.markAmexText}>AMEX</Text>
      </View>
    );
  }
  if (id === 'rupay') {
    return (
      <Image
        source={rupayLogo}
        style={styles.markRupayImage}
        resizeMode="contain"
        accessibilityLabel="RuPay"
      />
    );
  }
  if (id === 'diner') {
    return (
      <View style={[styles.mark, styles.markDiner]}>
        <Text style={styles.markDinerText}>DINERS</Text>
      </View>
    );
  }
  if (id === 'apple_pay') {
    return (
      <View style={[styles.mark, styles.markApple]}>
        <MaterialCommunityIcons name="apple" size={11} color="#fff" />
        <Text style={styles.markAppleText}>Pay</Text>
      </View>
    );
  }
  if (id === 'paypal') {
    return (
      <View style={[styles.mark, styles.markPaypal]}>
        <FontAwesome name="paypal" size={14} color="#003087" />
      </View>
    );
  }
  return null;
};

const InternationalPaymentMethodSelector = ({value, onChange}: Props) => {
  return (
    <View style={styles.wrapper}>
      <View style={styles.headingRow}>
        <Text style={styles.label}>Preferred payment method</Text>
        <Text style={styles.optional}>Optional</Text>
      </View>
      <Text style={styles.helper}>
        Optional — pick PayPal for a faster checkout. A {PAYPAL_SURCHARGE_PCT}%
        processing surcharge applies.
      </Text>
      <View style={styles.options}>
        {OPTIONS.map(option => {
          const selected = value === option.value;
          const hasFee = option.surchargePct > 0;
          return (
            <Pressable
              key={option.value}
              onPress={() => onChange(selected ? undefined : option.value)}
              style={[styles.chip, selected && styles.chipSelected]}>
              <View style={[styles.tick, selected && styles.tickSelected]}>
                {selected && (
                  <MaterialCommunityIcons name="check" size={10} color="#fff" />
                )}
              </View>
              <BrandMark id={option.value} />
              <Text
                style={[styles.chipText, selected && styles.chipTextSelected]}>
                {option.label}
              </Text>
              {hasFee && (
                <View
                  style={[
                    styles.feeBadge,
                    selected && styles.feeBadgeSelected,
                  ]}>
                  <Text style={styles.feeBadgeText}>
                    +{option.surchargePct}%
                  </Text>
                </View>
              )}
            </Pressable>
          );
        })}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    marginTop: 12,
    marginBottom: 8,
  },
  headingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 4,
  },
  label: {
    fontSize: 13,
    fontWeight: '600',
    color: '#262322',
  },
  optional: {
    color: '#888280',
    backgroundColor: '#F5EDD3',
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 2,
    fontSize: 10,
    fontWeight: '600',
    letterSpacing: 0.4,
  },
  helper: {
    fontSize: 12,
    lineHeight: 17,
    color: '#888280',
    marginBottom: 12,
  },
  options: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    borderWidth: 1.5,
    borderColor: 'rgba(38,35,34,0.10)',
    borderRadius: 12,
    paddingLeft: 11,
    paddingRight: 14,
    paddingVertical: 9,
    backgroundColor: '#fff',
  },
  chipSelected: {
    borderColor: '#3DAB9E',
    backgroundColor: '#E4F6EB',
  },
  tick: {
    width: 16,
    height: 16,
    borderWidth: 1.5,
    borderColor: 'rgba(77,63,55,0.22)',
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tickSelected: {
    backgroundColor: '#3DAB9E',
    borderColor: '#3DAB9E',
  },
  chipText: {
    fontSize: 13,
    fontWeight: '500',
    color: '#262322',
  },
  chipTextSelected: {
    color: '#262322',
    fontWeight: '600',
  },
  feeBadge: {
    backgroundColor: '#FFEFEE',
    borderRadius: 999,
    paddingHorizontal: 7,
    paddingVertical: 2,
    marginLeft: 2,
  },
  feeBadgeSelected: {
    backgroundColor: 'rgba(225,120,70,0.18)',
  },
  feeBadgeText: {
    color: '#E17B21',
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.2,
  },
  /* Brand marks — built from primitives, no copied logos */
  mark: {
    width: 34,
    height: 22,
    borderRadius: 5,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
  },
  markVisa: {
    backgroundColor: '#1A1F71',
  },
  markVisaText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: '800',
    fontStyle: 'italic',
    letterSpacing: 0.6,
  },
  markMc: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: 'rgba(38,35,34,0.10)',
  },
  mcCircle: {
    width: 13,
    height: 13,
    borderRadius: 999,
  },
  markAmex: {
    backgroundColor: '#006FCF',
  },
  markAmexText: {
    color: '#fff',
    fontSize: 8,
    fontWeight: '800',
    letterSpacing: 0.4,
  },
  markRupayImage: {
    width: 38,
    height: 22,
  },
  markDiner: {
    backgroundColor: '#0079BE',
  },
  markDinerText: {
    color: '#fff',
    fontSize: 7,
    fontWeight: '800',
    letterSpacing: 0.2,
  },
  markApple: {
    backgroundColor: '#111',
    gap: 1,
  },
  markAppleText: {
    color: '#fff',
    fontSize: 8,
    fontWeight: '700',
  },
  markPaypal: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: 'rgba(38,35,34,0.10)',
  },
});

export default InternationalPaymentMethodSelector;
